/*
 * verification.js
 * ----------------
 * Upload logic for the verification page and compact verification widgets.
 *
 *  1. On load, GET /apps/verify/verification-status (through the Shopify App Proxy).
 *     The response says which required documents are on file; we paint each slot.
 *  2. Each file input fires a POST to /apps/verify/verification-upload with a
 *     multipart body containing the file, document_type, and filename.
 *  3. The server updates the customer's `custom.purchase_verified` metafield
 *     synchronously, so by the time the last upload resolves the cart/product
 *     gates on subsequent page loads are already unlocked.
 */

(function () {
  var roots = document.querySelectorAll('[data-verification-root]');
  if (!roots.length) return;

  function toArray(listLike) {
    return Array.prototype.slice.call(listLike || []).filter(function (file) {
      return file instanceof File;
    });
  }

  function clipboardFiles(clipboardData) {
    var files = toArray(clipboardData && clipboardData.files);
    if (files.length) return files;

    return Array.prototype.slice.call((clipboardData && clipboardData.items) || [])
      .map(function (item) {
        return item && typeof item.getAsFile === 'function' ? item.getAsFile() : null;
      })
      .filter(function (file) {
        return file instanceof File;
      });
  }

  function fileTypeMessage(key) {
    if (key === 'prescription') {
      return 'That file type isn\'t supported. Use JPEG, PNG, or PDF.';
    }
    return 'That file type isn\'t supported. Use JPEG or PNG.';
  }

  function createController(root) {
    var statusUrl = root.getAttribute('data-status-url');
    var uploadUrl = root.getAttribute('data-upload-url');
    var slots = {};
    var progressEl = root.querySelector('[data-progress]');
    var progressKickerEl = root.querySelector('[data-progress-kicker]');
    var progressTitleEl = root.querySelector('[data-progress-title]');
    var progressCopyEl = root.querySelector('[data-progress-copy]');
    var continueBtn = root.querySelector('[data-continue-btn]');
    var profileLink = root.querySelector('[data-profile-link]');
    var railSteps = {
      upload: root.querySelector('[data-rail-step="upload"]'),
      review: root.querySelector('[data-rail-step="review"]'),
      continue: root.querySelector('[data-rail-step="continue"]')
    };
    var summarySteps = {
      upload: root.querySelector('[data-summary-step="upload"]'),
      review: root.querySelector('[data-summary-step="review"]'),
      continue: root.querySelector('[data-summary-step="continue"]')
    };

    root.querySelectorAll('[data-slot]').forEach(function (el) {
      var key = el.getAttribute('data-slot');
      var pasteHint = el.querySelector('[data-paste-hint]');
      var actions = el.querySelector('.verification__actions');

      if (actions) {
        Array.prototype.slice.call(actions.querySelectorAll('button, [role="button"]'))
          .forEach(function (button) {
            var label = (button.textContent || '').trim().toLowerCase();
            if (label.indexOf('paste') !== -1) {
              button.remove();
            }
          });
      }

      slots[key] = {
        el: el,
        input: el.querySelector('[data-slot-input]'),
        label: el.querySelector('[data-upload-label]'),
        idleLabel: el.querySelector('[data-idle-label]'),
        busyLabel: el.querySelector('[data-busy-label]'),
        errorEl: el.querySelector('[data-slot-error]'),
        pill: el.querySelector('[data-pill]'),
        surface: el.querySelector('[data-upload-surface]'),
        pasteHint: pasteHint,
        pasteHintDefault: pasteHint ? pasteHint.textContent : '',
        pasteCatcher: el.querySelector('[data-paste-catcher]'),
        dragDepth: 0
      };
    });
    var slotKeys = Object.keys(slots);

    function setStepState(collection, currentKey, completedKeys) {
      Object.keys(collection).forEach(function (key) {
        var step = collection[key];
        if (!step) return;
        step.classList.remove('is-current', 'is-complete');
        if (completedKeys.indexOf(key) !== -1) {
          step.classList.add('is-complete');
        } else if (key === currentKey) {
          step.classList.add('is-current');
        }
      });
    }

    function setSlotPill(key, state) {
      var slot = slots[key];
      if (!slot) return;
      slot.pill.classList.remove(
        'verification__pill--pending',
        'verification__pill--uploaded',
        'verification__pill--error',
        'verification__pill--busy'
      );
      if (state === 'uploaded') {
        slot.pill.classList.add('verification__pill--uploaded');
        slot.pill.textContent = 'On file';
        slot.el.classList.add('is-done');
        slot.idleLabel.textContent = 'Add more files';
      } else if (state === 'busy') {
        slot.pill.classList.add('verification__pill--busy');
        slot.pill.textContent = 'Uploading';
      } else if (state === 'error') {
        slot.pill.classList.add('verification__pill--error');
        slot.pill.textContent = 'Needs attention';
        slot.el.classList.remove('is-done');
      } else {
        slot.pill.classList.add('verification__pill--pending');
        slot.pill.textContent = 'Ready to upload';
        slot.el.classList.remove('is-done');
      }
    }

    function setSlotBusy(key, busy, busyLabel) {
      var slot = slots[key];
      if (!slot) return;
      slot.label.setAttribute('aria-busy', busy ? 'true' : 'false');
      slot.input.disabled = !!busy;
      slot.idleLabel.hidden = !!busy;
      slot.busyLabel.hidden = !busy;
      if (busy && busyLabel) slot.busyLabel.textContent = busyLabel;
      if (!busy) slot.busyLabel.textContent = 'Uploading...';
    }

    function showSlotError(key, message) {
      var slot = slots[key];
      if (!slot) return;
      if (message) {
        slot.errorEl.textContent = message;
        slot.errorEl.hidden = false;
      } else {
        slot.errorEl.textContent = '';
        slot.errorEl.hidden = true;
      }
    }

    function setPasteHint(key, message) {
      var slot = slots[key];
      if (!slot || !slot.pasteHint) return;
      slot.pasteHint.textContent = message || slot.pasteHintDefault;
    }

    function resetPasteState(key) {
      var slot = slots[key];
      if (!slot) return;
      slot.el.classList.remove('is-paste-ready');
      setPasteHint(key, slot.pasteHintDefault);
    }

    function armPasteMode(key) {
      var slot = slots[key];
      if (!slot || !slot.pasteCatcher || slot.input.disabled) return;
      Object.keys(slots).forEach(resetPasteState);
      slot.el.classList.add('is-paste-ready');
      setPasteHint(key, 'Paste now with Command+V or Ctrl+V.');
      slot.pasteCatcher.focus();
    }

    function repaint(status) {
      var done = 0;
      var total = slotKeys.length;
      var slotStatus = {
        prescription: !!status.has_prescription,
        medical_id_front: !!status.has_medical_id_front,
        medical_id_back: !!status.has_medical_id_back
      };

      slotKeys.forEach(function (key) {
        var uploaded = !!slotStatus[key];
        setSlotPill(key, uploaded ? 'uploaded' : 'pending');
        if (uploaded) done++;
      });

      if (progressEl) {
        if (total > 0 && done === total) {
          progressEl.setAttribute('data-state', 'success');
          if (progressKickerEl) progressKickerEl.textContent = 'You are all set';
          if (progressTitleEl) progressTitleEl.textContent = 'Your prescription is on file.';
          if (progressCopyEl) {
            progressCopyEl.textContent = 'You are done with verification. Continue to the store now, and if you ever need to update your prescription later you can still add more files here.';
          }
          setStepState(railSteps, null, ['upload', 'review', 'continue']);
          setStepState(summarySteps, null, ['upload', 'review', 'continue']);
        } else {
          progressEl.setAttribute('data-state', 'pending');
          if (progressKickerEl) progressKickerEl.textContent = 'Almost done';
          if (progressTitleEl) progressTitleEl.textContent = 'Upload your prescription to unlock ordering.';
          if (progressCopyEl) {
            progressCopyEl.textContent = 'Once your prescription is uploaded, this page confirms it is on file and unlocks the continue button.';
          }
          setStepState(railSteps, 'upload', []);
          setStepState(summarySteps, 'upload', []);
        }
      }

      if (continueBtn) {
        if (total > 0 && done === total) {
          continueBtn.removeAttribute('aria-disabled');
          continueBtn.classList.add('is-ready');
        } else {
          continueBtn.setAttribute('aria-disabled', 'true');
          continueBtn.classList.remove('is-ready');
        }
      }

      if (profileLink) {
        if (total > 0 && done === total) {
          profileLink.removeAttribute('aria-disabled');
        } else {
          profileLink.setAttribute('aria-disabled', 'true');
        }
      }
    }

    function fetchStatus() {
      return fetch(statusUrl, { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('status ' + response.status);
          return response.json();
        })
        .then(function (body) {
          if (body && body.status) repaint(body.status);
        })
        .catch(function (err) {
          console.error('verification status failed', err);
          if (progressEl) {
            progressEl.setAttribute('data-state', 'error');
            if (progressKickerEl) progressKickerEl.textContent = 'Something went wrong';
            if (progressTitleEl) progressTitleEl.textContent = "We couldn't check your documents.";
            if (progressCopyEl) progressCopyEl.textContent = 'Please reload the page and try again.';
          }
          setStepState(railSteps, 'upload', []);
          setStepState(summarySteps, 'upload', []);
        });
    }

    function uploadSingleFile(key, file) {
      var fd = new FormData();
      fd.append('file', file, file.name);
      fd.append('document_type', key);
      fd.append('file_name', file.name);

      return fetch(uploadUrl, {
        method: 'POST',
        body: fd,
        credentials: 'same-origin'
      })
        .then(function (response) {
          return response.json().then(function (body) {
            return { ok: response.ok, status: response.status, body: body };
          });
        })
        .then(function (result) {
          if (!result.ok) {
            var msg = 'Upload failed.';
            if (result.status === 413) msg = 'File too large. Must be 10 MB or smaller.';
            else if (result.status === 415) msg = fileTypeMessage(key);
            else if (result.status === 401) msg = 'You need to be logged in. Please refresh and log in.';
            else if (result.body && result.body.error) msg = result.body.error;
            return { ok: false, message: msg };
          }

          if (result.body && result.body.status) {
            repaint(result.body.status);
          } else {
            setSlotPill(key, 'uploaded');
          }

          return { ok: true };
        })
        .catch(function (err) {
          console.error('upload failed', err);
          return { ok: false, message: 'Upload failed. Please try again.' };
        });
    }

    function uploadFiles(key, files) {
      var queue = toArray(files);
      if (!queue.length) return Promise.resolve();

      showSlotError(key, null);
      resetPasteState(key);
      setSlotBusy(
        key,
        true,
        queue.length > 1 ? 'Uploading ' + queue.length + ' files...' : 'Uploading...'
      );
      setSlotPill(key, 'busy');
      if (progressEl) {
        progressEl.setAttribute('data-state', 'pending');
        if (progressKickerEl) progressKickerEl.textContent = 'Uploading now';
        if (progressTitleEl) {
          progressTitleEl.textContent = queue.length > 1
            ? 'We are uploading your files.'
            : 'We are uploading your prescription.';
        }
        if (progressCopyEl) {
          progressCopyEl.textContent = 'Stay on this page for a moment while we save your file and confirm it is on record.';
        }
      }
      setStepState(railSteps, 'review', ['upload']);
      setStepState(summarySteps, 'review', ['upload']);

      var successes = 0;
      var failures = [];

      return queue.reduce(function (promise, file) {
        return promise.then(function () {
          return uploadSingleFile(key, file).then(function (result) {
            if (result.ok) {
              successes += 1;
            } else {
              failures.push(result.message);
            }
          });
        });
      }, Promise.resolve())
        .then(function () {
          if (failures.length && successes === 0) {
            setSlotPill(key, 'error');
            showSlotError(key, failures[0]);
            return;
          }

          if (failures.length) {
            showSlotError(
              key,
              'Uploaded ' + successes + ' of ' + queue.length + ' files. ' + failures[0]
            );
            return;
          }

        })
        .then(function () {
          setSlotBusy(key, false);
        });
    }

    Object.keys(slots).forEach(function (key) {
      var slot = slots[key];

      slot.input.addEventListener('change', function (event) {
        var files = toArray(event.target.files);
        if (!files.length) return;
        uploadFiles(key, files);
        event.target.value = '';
      });

      if (slot.surface) {
        slot.surface.addEventListener('click', function (event) {
          var target = event.target && event.target.nodeType === 1
            ? event.target
            : event.target && event.target.parentElement;
          if (target && target.closest('label, input, button, a')) return;
          armPasteMode(key);
        });

        slot.surface.addEventListener('keydown', function (event) {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          armPasteMode(key);
        });

        slot.surface.addEventListener('dragenter', function (event) {
          event.preventDefault();
          slot.dragDepth += 1;
          slot.el.classList.add('is-dragging');
        });

        slot.surface.addEventListener('dragover', function (event) {
          event.preventDefault();
          slot.el.classList.add('is-dragging');
        });

        slot.surface.addEventListener('dragleave', function (event) {
          event.preventDefault();
          slot.dragDepth = Math.max(0, slot.dragDepth - 1);
          if (slot.dragDepth === 0) {
            slot.el.classList.remove('is-dragging');
          }
        });

        slot.surface.addEventListener('drop', function (event) {
          event.preventDefault();
          slot.dragDepth = 0;
          slot.el.classList.remove('is-dragging');
          uploadFiles(key, toArray(event.dataTransfer && event.dataTransfer.files));
        });
      }

      if (slot.pasteCatcher) {
        slot.pasteCatcher.addEventListener('paste', function (event) {
          var files = clipboardFiles(event.clipboardData);
          event.preventDefault();
          if (!files.length) {
            showSlotError(key, 'Your clipboard does not contain an image or PDF file.');
            return;
          }
          uploadFiles(key, files);
        });

        slot.pasteCatcher.addEventListener('blur', function () {
          window.setTimeout(function () {
            resetPasteState(key);
          }, 75);
        });
      }
    });

    if (continueBtn) {
      continueBtn.addEventListener('click', function (event) {
        if (continueBtn.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
        }
      });
    }

    fetchStatus();
  }

  roots.forEach(createController);
}());
